import React, { useState } from "react";
import { Box, ButtonBase, Skeleton, Typography } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { ACCORDION_BUTTON_SX, CARD_SURFACE_SX, MOTION, motionSx } from "../../../styles/tokens";

// Зазор между сегментами кольца, px по длине дуги
const SEGMENT_GAP = 2;
// Смена долей при смене периода или фильтра — плавно за MOTION.data (значения в style, переход — CSS)
const RING_SX = { "& .donut-segment": motionSx(["stroke-dasharray", "stroke-dashoffset", "stroke"], MOTION.data) };
// Проценты легенды: отдельная колонка, моноширинные цифры, вправо
const PERCENT_SX = { fontVariantNumeric: "tabular-nums", textAlign: "right", whiteSpace: "nowrap" };

// Простая donut диаграмма на SVG с легендой справа или снизу
// Скелетон по форме диаграммы: кольцо (маской из круга) и строки легенды
function DonutSkeleton({ size, legendBelow, noBackground }) {
  const hole = size / 2 - 18; // как stroke у кольца
  const ring = `radial-gradient(circle, transparent ${hole}px, #000 ${hole + 0.5}px)`;
  const legend = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9, width: legendBelow ? "100%" : 140 }}>
      {[0.9, 0.7, 0.8].map((w) => (
        <Skeleton key={w} variant="rounded" height={12} width={`${w * 100}%`} sx={{ borderRadius: "6px" }} />
      ))}
    </Box>
  );
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: "flex",
        flexDirection: legendBelow ? "column" : "row",
        alignItems: "center",
        gap: legendBelow ? 1.5 : 3,
        ...(noBackground ? {} : { ...CARD_SURFACE_SX, p: 2, minHeight: 140 }),
      }}
    >
      <Skeleton variant="circular" width={size} height={size} sx={{ flexShrink: 0, maskImage: ring, WebkitMaskImage: ring }} />
      {legend}
    </Box>
  );
}

const DonutCard = ({ title, data = [], colors = [], size = 120, variant = "donut", showLegendBelow = false, expandableContent = null, expandableTitle = null, noBackground = false, maxLegendItems = null, loading = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (loading) return <DonutSkeleton size={size} legendBelow={showLegendBelow} noBackground={noBackground} />;
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  const radius = size / 2;
  const stroke = 18;
  const r = radius - stroke / 2;
  const circumference = 2 * Math.PI * r;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const polar = (cx, cy, rad, angleDeg) => {
    const a = toRad(angleDeg);
    return { x: cx + rad * Math.cos(a), y: cy + rad * Math.sin(a) };
  };

  let offset = 0;
  // Зазор нужен, только если сегментов больше одного
  const gap = data.filter((d) => (Number(d.value) || 0) > 0).length > 1 ? SEGMENT_GAP : 0;

  const items = data.map((d, i) => {
    const value = Number(d.value) || 0;
    const pct = total > 0 ? (value / total) : 0;
    const len = pct * circumference;
    const visibleLen = Math.max(len - gap, 0);
    const item = {
      ...d,
      value,
      color: colors[i % colors.length] || `hsl(${(i * 70) % 360} 70% 55%)`,
      dasharray: `${visibleLen} ${circumference - visibleLen}`,
      dashoffset: circumference - offset,
      percent: pct * 100,
      angle: pct * 360,
    };
    offset += len;
    return item;
  });
  const allLegendItems = [...items].sort((a, b) => (b.value || 0) - (a.value || 0));
  const legendItems = maxLegendItems ? allLegendItems.slice(0, maxLegendItems) : allLegendItems;

  // Создаем мапу цветов на основе исходного порядка data (как в MetricCard)
  const colorMap = new Map();
  if (data && data.length > 0) {
    data.forEach((item, index) => {
      const label = item.label || item.name || "Не указан";
      const color = colors[index % colors.length];
      colorMap.set(label, color);
    });
  }
  
  // Логирование для отладки
  if (expandableContent && expandableContent.length > 0) {
    console.log("🔍 DonutCard Debug:");
    console.log("  Data labels:", data.map(d => d.label || d.name || "Не указан"));
    console.log("  ColorMap keys:", Array.from(colorMap.keys()));
    console.log("  ExpandableContent labels:", expandableContent.map(item => item.label || item.name || "Не указан"));
  }

  const formatCenterTitle = (t) => {
    if (!t) return "";
    if (t.includes(" / ")) return t.replace(" / ", "\n");
    const parts = t.split(" ");
    if (parts.length >= 2) {
      const mid = Math.ceil(parts.length / 2);
      return parts.slice(0, mid).join(" ") + "\n" + parts.slice(mid).join(" ");
    }
    // fallback: жестко переносим середину строки
    const midc = Math.floor(t.length / 2);
    return t.slice(0, midc) + "\n" + t.slice(midc);
  };

  // Если легенда снизу, используем вертикальную компоновку
  if (showLegendBelow) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
        {/* Донат */}
        <Box sx={{ position: "relative", width: size, height: size, ...RING_SX }}>
          {variant === "donut" ? (
            <>
              <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
                   style={{ transform: "rotate(-90deg)" }}>
                <circle cx={radius} cy={radius} r={r}
                        stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
                {items.map((it, idx) => (
                  <circle key={idx}
                          cx={radius} cy={radius} r={r}
                          stroke={it.color}
                          strokeWidth={stroke}
                          className="donut-segment"
                          style={{ strokeDasharray: it.dasharray, strokeDashoffset: it.dashoffset }}
                          strokeLinecap="butt"
                          fill="none" />
                ))}
              </svg>
              {/* Заголовок в центре доната */}
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                {!!title && (
                  <Typography sx={{ color: "#fff", fontSize: "0.8rem", fontWeight: 600, textAlign: "center", px: 0.5, lineHeight: 1.15, whiteSpace: "pre-line", maxWidth: size * 0.6 }}>
                    {formatCenterTitle(title)}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              {(() => {
                let startAngle = -90;
                return items.map((it, idx) => {
                  const endAngle = startAngle + it.angle;
                  const start = polar(radius, radius, radius, startAngle);
                  const end = polar(radius, radius, radius, endAngle);
                  const largeArc = it.angle > 180 ? 1 : 0;
                  const d = [
                    `M ${radius} ${radius}`,
                    `L ${start.x} ${start.y}`,
                    `A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`,
                    'Z',
                  ].join(' ');
                  startAngle = endAngle;
                  return <path key={idx} d={d} fill={it.color} stroke="#121212" strokeWidth={1} />;
                });
              })()}
            </svg>
          )}
        </Box>

        {/* Легенда снизу */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, width: "100%" }}>
          {legendItems.map((it, idx) => (
            <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", columnGap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 999, background: it.color }} />
              <Typography sx={{ color: "#fff", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                {it.legendLabel || it.label}
              </Typography>
              <Typography sx={{ color: "#fff", fontSize: "0.85rem", fontWeight: 700, ...PERCENT_SX }}>{Math.round(it.percent)}%</Typography>
            </Box>
          ))}
        </Box>

        {/* Раскрывающаяся секция с источниками заказа */}
        {expandableContent && expandableContent.length > 0 && expandableTitle && (
          <>
            <Box sx={{ width: "100%", mt: 1, pt: 1, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <ButtonBase
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              sx={ACCORDION_BUTTON_SX}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "rgba(255,255,255,0.9)",
                  fontWeight: 500,
                }}
              >
                {expandableTitle}
              </Typography>
              <KeyboardArrowDown
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.9)",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </ButtonBase>
            </Box>
            <Box
              sx={{
                maxHeight: isExpanded ? "1000px" : "0px",
                opacity: isExpanded ? 1 : 0,
                overflow: "hidden",
                transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out",
                mt: isExpanded ? 1 : 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {expandableContent.map((item, index) => {
                const itemColor = colors[index % colors.length] || `hsl(${(index * 70) % 360} 70% 55%)`;
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.4,
                      px: 1,
                      py: 0.8,
                      borderRadius: 1,
                      background: `${itemColor}15`,
                      border: `1px solid ${itemColor}40`,
                      borderLeft: `4px solid ${itemColor}`,
                      opacity: isExpanded ? 1 : 0,
                      transform: isExpanded ? "translateY(0)" : "translateY(-10px)",
                      transition: `opacity 0.3s ease ${index * 0.03}s, transform 0.3s ease ${index * 0.03}s`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.9)", fontWeight: 500 }}>
                        {item.label}:
                      </Typography>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#fff" }}>
                        {item.ordersCount !== undefined && item.ordersCount > 0 
                          ? `${item.ordersCount} ${item.ordersCount === 1 ? "заказ" : item.ordersCount < 5 ? "заказа" : "заказов"}`
                          : "0 заказов"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    );
  }

  // Оригинальная компоновка (легенда справа)
  return (
    <Box
      sx={{
        ...(noBackground ? {} : { ...CARD_SURFACE_SX, p: 2 }),
        display: "flex",
        alignItems: "center",
        gap: 2,
        ...(noBackground ? {} : { minHeight: 140 }),
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
          <Box sx={{ position: "relative", width: size, height: size, ...RING_SX }}>
            {variant === "donut" ? (
              <>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
                     style={{ transform: "rotate(-90deg)" }}>
                  <circle cx={radius} cy={radius} r={r}
                          stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
                  {items.map((it, idx) => (
                    <circle key={idx}
                            cx={radius} cy={radius} r={r}
                            stroke={it.color}
                            strokeWidth={stroke}
                            className="donut-segment"
                            style={{ strokeDasharray: it.dasharray, strokeDashoffset: it.dashoffset }}
                            strokeLinecap="butt"
                            fill="none" />
                  ))}
                </svg>
                {/* Заголовок в центре доната */}
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                  {!!title && (
                    <Typography sx={{ color: "#fff", fontSize: "0.8rem", fontWeight: 600, textAlign: "center", px: 0.5, lineHeight: 1.15, whiteSpace: "pre-line", maxWidth: size * 0.6 }}>
                      {formatCenterTitle(title)}
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {(() => {
                  let startAngle = -90; // начинаем сверху
                  return items.map((it, idx) => {
                    const endAngle = startAngle + it.angle;
                    const start = polar(radius, radius, radius, startAngle);
                    const end = polar(radius, radius, radius, endAngle);
                    const largeArc = it.angle > 180 ? 1 : 0;
                    const d = [
                      `M ${radius} ${radius}`,
                      `L ${start.x} ${start.y}`,
                      `A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`,
                      'Z',
                    ].join(' ');
                    startAngle = endAngle;
                    return <path key={idx} d={d} fill={it.color} stroke="#121212" strokeWidth={1} />;
                  });
                })()}
              </svg>
            )}
          </Box>

          <Box sx={{ display: "grid", gridAutoRows: "minmax(0,1fr)", rowGap: 0.6, flex: 1, minWidth: 0 }}>
            {legendItems.map((it, idx) => (
              <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", columnGap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 999, background: it.color }} />
                <Typography sx={{ color: "#fff", fontSize: "0.9rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {it.legendLabel || it.label}
                </Typography>
                <Typography sx={{ color: "#fff", fontSize: "0.9rem", fontWeight: 700, ...PERCENT_SX }}>{Math.round(it.percent)}%</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Раскрывающаяся секция с остальными типами оплат */}
        {expandableContent && expandableContent.length > 0 && (
          <>
            <Box sx={{ width: "100%", mt: 1, pt: 1, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <ButtonBase
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              sx={ACCORDION_BUTTON_SX}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "rgba(255,255,255,0.9)",
                  fontWeight: 500,
                }}
              >
                {isExpanded ? "Скрыть остальные типы оплат" : `Показать остальные типы оплат (${expandableContent.length})`}
              </Typography>
              <KeyboardArrowDown
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.9)",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </ButtonBase>
            </Box>
            <Box
              sx={{
                maxHeight: isExpanded ? "1000px" : "0px",
                opacity: isExpanded ? 1 : 0,
                overflow: "hidden",
                transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out",
                mt: isExpanded ? 1.5 : 0,
                display: "flex",
                flexDirection: "column",
                gap: 0.6,
              }}
            >
              {expandableContent.map((item, index) => {
                // Используем мапу цветов для определения цвета по label
                const label = item.label || item.name || "Не указан";
                let itemColor = colorMap.get(label);
                
                // Логирование для отладки
                if (!itemColor) {
                  console.log(`  ⚠️ Color not found for label: "${label}"`);
                  console.log(`     Available labels:`, Array.from(colorMap.keys()));
                  // Пробуем найти по частичному совпадению
                  for (const [mapLabel, mapColor] of colorMap.entries()) {
                    if (label.toLowerCase().includes(mapLabel.toLowerCase()) || 
                        mapLabel.toLowerCase().includes(label.toLowerCase())) {
                      console.log(`     Found partial match: "${mapLabel}" -> using its color`);
                      itemColor = mapColor;
                      break;
                    }
                  }
                }
                
                // Если цвет не найден, используем fallback
                if (!itemColor) {
                  itemColor = colors[(index + 4) % colors.length];
                  console.log(`     Using fallback color for index ${index + 4}`);
                } else {
                  console.log(`  ✅ Color found for "${label}": ${itemColor}`);
                }
                
                // Вычисляем процент от общей суммы
                const percent = total > 0 ? ((Number(item.value) || 0) / total) * 100 : 0;

                return (
                  <Box
                    key={`${item.label || item.name || index}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.2,
                      py: 0.8,
                      borderRadius: 1,
                      background: `${itemColor}15`,
                      border: `1px solid ${itemColor}40`,
                      borderLeft: `4px solid ${itemColor}`,
                      opacity: isExpanded ? 1 : 0,
                      transform: isExpanded ? "translateY(0)" : "translateY(-10px)",
                      transition: `opacity 0.3s ease ${index * 0.03}s, transform 0.3s ease ${index * 0.03}s`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: itemColor,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: "rgba(255,255,255,0.9)",
                        }}
                      >
                        {item.label || item.name || "Не указан"}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: "rgba(255,255,255,0.8)",
                        fontWeight: 500,
                        ...PERCENT_SX,
                      }}
                    >
                      {percent > 0
                        ? `${Number(percent).toLocaleString("ru-RU", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}%`
                        : `${Number(item.value || item.sum || 0).toLocaleString("ru-RU", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })} ₽`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DonutCard;


